import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import { getGoHireRoute, getCreateJobRouteWithJobType } from "../../../../routers/routeUtils";

import { CreateJobHeader } from "../../components";

import BackgroundImg from "../../assets/images/create-job-bg.png";
import TalentDiscoveryImg from "../../assets/images/talent-discovery-img.png";
import ProfessionalRecruitmentImg from "../../assets/images/professional-recruitment-img.png";

import CreateTalentJob from "./CreateTalentJob";
import CreateProfessionalJob from "./CreateProfessionalJob";

const CreateJobPage = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage" });

  const navigate = useNavigate();
  const handleGoBack = () => navigate(getGoHireRoute());
  const handleGoToTalentDiscovery = () => navigate(getCreateJobRouteWithJobType("talent"));
  const handleGoToProfessionalRecruitment = () => navigate(getCreateJobRouteWithJobType("professional"));

  const jobCategories = [
    {
      title: t("talentDiscovery"),
      description: t("identifyAndEvaluatePromisingFootballTalents"),
      figure: TalentDiscoveryImg,
      handler: handleGoToTalentDiscovery,
      disabled: true,
    },
    {
      title: t("professionalRecruitment"),
      description: t("hireQualifiedFootballProfessionals"),
      figure: ProfessionalRecruitmentImg,
      handler: handleGoToProfessionalRecruitment,
    },
  ];

  const [searchParams] = useSearchParams();
  const jobType = searchParams.get("jobType") as "talent" | "professional";

  if (jobType === "talent") return <CreateTalentJob />;
  if (jobType === "professional") return <CreateProfessionalJob />;

  return (
    <Box sx={styles.mainContainer}>
      <CreateJobHeader handleGoBack={handleGoBack} />
      <Box sx={styles.containerWrapper}>
        <Box sx={styles.container}>
          <Box sx={styles.headerContainer}>
            <Box sx={styles.headerContent}>
              <Typography component="h2" sx={styles.headerTitle}>
                {t("hiringMission")}
              </Typography>
              <Typography component="p" sx={styles.headerDescription}>
                {t("chooseWhoYouAreHiring")}
              </Typography>
            </Box>
            <Box sx={styles.jobCategoriesContainer}>
              {jobCategories.map((category) => (
                <JobCategoryCard key={category.title} category={category} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateJobPage;

const JobCategoryCard = ({
  category: { title, description, figure, handler, disabled },
}: {
  category: { title: string; description: string; figure: string; handler: () => void; disabled?: boolean };
}) => {
  return (
    <Box sx={styles.jobCategoryCard(disabled)} onClick={disabled ? undefined : handler} component="button">
      <Box component="img" src={figure} alt={title} sx={styles.jobCategoryFigure} />
      <Box sx={styles.jobCategoryContent}>
        <Typography component="h3" sx={styles.jobCategoryTitle}>
          {title}
        </Typography>
        <Typography component="p" sx={styles.jobCategoryDescription}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const styles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    background: "#FFF",
    minHeight: "100vh",
  },
  containerWrapper: {
    backgroundImage: `url(${BackgroundImg})`,
    backgroundSize: "contain",
    backgroundPosition: "bottom center",
    backgroundRepeat: "no-repeat",
    paddingTop: "80px",
    height: "calc(100vh - 80px)",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",

    width: "920px",
    padding: "40px 24px",
    borderRadius: "12px",
    background: "#FFF",
    boxShadow: "0 2px 30px 0 rgba(20, 21, 26, 0.05)",

    margin: "0 auto",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "40px",
  },
  headerContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  headerTitle: {
    color: "var(--Brand-600, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "40px",
    fontStyle: "normal",
    fontWeight: 850,
    lineHeight: "120%",
    textTransform: "uppercase",
  },
  headerDescription: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  jobCategoriesContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "24px",
  },
  jobCategoryCard: (disabled?: boolean) => ({
    textDecoration: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",

    borderRadius: "12px",
    border: "2px solid #E7E7E7",

    background: "#FAFFFA",
    boxShadow: "0 12px 20px 0 rgba(0, 0, 0, 0.04), 0 0 2px 0 rgba(0, 0, 0, 0.06)",
    width: "360px",
    padding: "32px",

    cursor: "pointer",
    "&:hover": {
      border: "2px solid var(--Brand-600, #008B07)",
    },
    ...(disabled && {
      cursor: "default !important",
      border: "2px solid #E7E7E7 !important",
      background: "#E7E7E7 !important",
      color: "var(--Dark---600, #707070) !important",
    }),
  }),
  jobCategoryContent: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  jobCategoryFigure: {
    width: "240px",
    height: "180px",
  },
  jobCategoryTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    textAlign: "center",
  },
  jobCategoryDescription: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    textAlign: "center",
  },
};
